import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Checkbox, Col, PageHeader, Popconfirm, Row, Select, Table,} from 'antd';
import {DeleteOutlined, ReloadOutlined,} from '@ant-design/icons';
import _ from 'lodash';
import {
  createLiftoffMapping,
  deleteLiftoffMapping,
  getLiftoffMapping,
  updateLiftoffMapping
} from '../../api/liftoffMappingApi';
import {useHistory} from 'react-router-dom';
import {LiftoffMappingGroups} from './mappings';
import {CreateLiftoffMappingButton} from './CreateLiftoffMappingButton';
import moment from 'moment';
import {PREFERRED_DATETIME_FORMAT} from '../../util/time';
import {EditLiftoffMappingButton} from './EditLiftoffMappingButton';
import sorters from "../../util/sorters";
import {handleResponse} from "../d365/utils";
import {useDynamicTableHeight} from '../../util/useDynamicTableHeight';
import {PSA_INSTANCE} from "../Constants";

const LiftoffMapping = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAuditing, setShowAuditing] = useState(false);
  const history = useHistory();
  const paths = history.location.pathname.split('/');
  const groupType = _.nth(paths, -2);
  const entityType = _.nth(paths, -1);
  const group = _.find(LiftoffMappingGroups, {group: groupType});
  const mapping = _.find(group.mappings, {entity: entityType});
  const [mappingData, setMappingData] = useState([]);
  const [warning, setWarning] = useState('');
  const [help, setHelp] = useState('');
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef, 100);
  const [instance, setInstance] = useState("CREDERA");

  const columns = [...mapping.fields.map(def => {
    if (def.type === 'divider') {
      return {type: 'divider'};
    }
    const col = {
      title: def.title,
      dataIndex: def.field,
    };

    if (def.render) {
      col.render = def.render;
    }
    if (def.width) {
      col.width = def.width;
    }
    if (def.sorter) {
      col.sorter = def.sorter;
    } else switch (def.type) {
      case 'text':
      case 'textarea':
        col.sorter = (a, b) => sorters.string(a[def.field], b[def.field]);
        break;
      case 'checkbox':
      case 'number':
        col.sorter = (a, b) => sorters.number(a[def.field], b[def.field]);
        break;
      case 'mapping':
        col.sorter = (a, b) => sorters.string(
          a[def.field] ? a[def.field][def.optionLabel] : null,
          b[def.field] ? b[def.field][def.optionLabel] : null);
        break;
      case 'date':
        col.sorter = (a, b) => sorters.date(a[def.field], b[def.field]);
        break;
      case 'select':
        col.sorter = (a, b) => sorters.string(a[def.field], b[def.field]);
        break;
      default:
        // ignore
        break;
    }

    return col;
  }),
    {
      title: 'Created By',
      dataIndex: 'CreatedBy',
      sorter: (a, b) => sorters.string(a.CreatedBy, b.CreatedBy),
      hidden: !showAuditing
    },
    {
      title: 'Created At',
      render: r => moment.utc(r.CreatedAt).local().format(PREFERRED_DATETIME_FORMAT),
      sorter: (a, b) => sorters.date(a.CreatedAt, b.CreatedAt),
      hidden: !showAuditing
    },
    {
      title: 'Updated By',
      render: r => r.UpdatedBy ? r.UpdatedBy : '-',
      sorter: (a, b) => sorters.string(a.UpdatedBy, b.UpdatedBy),
      hidden: !showAuditing
    },
    {
      title: 'Updated At',
      render: r => r.UpdatedAt ? moment.utc(r.UpdatedAt).local().format(PREFERRED_DATETIME_FORMAT) : '-',
      sorter: (a, b) => sorters.date(a.UpdatedAt, b.UpdatedAt),
      hidden: !showAuditing
    },
    {
      title: 'Actions',
      width: 230,
      render: e => (
        <>
          <EditLiftoffMappingButton
            entity={e}
            title={mapping.title}
            fields={mapping.fields}
            mappingData={mappingData}
            onComplete={updateEntity}
            style={{marginRight: 10}}
            disabled={mapping.noActionFunction ? mapping.noActionFunction(e) : false}
            psa_instance={mapping.multipleEntities ? instance : null}
          />
          <Popconfirm
            title={`Are you sure to remove ${mapping.remove_render === undefined ?
              e.Name === undefined ? 'this' : e.Name : mapping.remove_render(e)}?`}
            onConfirm={() => removeEntity(e)}
            okText="Yes"
            cancelText="No"
            disabled={mapping.noActionFunction ? mapping.noActionFunction(e) : false}
          >
            <Button
              disabled={mapping.noActionFunction ? mapping.noActionFunction(e) : false}
              icon={<DeleteOutlined/>}>Remove</Button>
          </Popconfirm>
        </>
      )
    }
  ].filter(item => !item.hidden);

  const buildRowsFromResponse = (res, mappingData) => {
    return res.map(r => {
      const row = {
        ID: r.ID,
        CreatedBy: r.CreatedBy,
        CreatedAt: r.CreatedAt,
        UpdatedBy: r.UpdatedBy,
        UpdatedAt: r.UpdatedAt
      };

      // Find each mapping entity value
      mapping.fields.forEach((f) => {
        if (f.type === 'mapping') {
          // For multiselect mappings, join values into a
          if (f.multi) {
            if (r[f.field]) {
              const values = (r[f.field]).split(',');
              row[f.field] = values.map(v => {
                return _.find(mappingData[f.field], {ID: _.toNumber(v)});
              });
            }
          } else {
            if (f.dataFrom) {
              row[f.field] = _.find(mappingData[f.dataFrom], {ID: r[f.idKey]});
            } else {
              row[f.field] = _.find(mappingData[f.field], {ID: r[f.idKey]});
            }
          }
        } else {
          row[f.field] = r[f.field];
        }
      });
      if (mapping.multipleEntities) {
        row.PsaInstance = r.PsaInstance;
      }

      return row;
    });
  };

  const buildEntityFromForm = form => {
    const entity = {};
    mapping.fields.forEach(f => {
      if (f.type === 'mapping') {
        // For multiselect mappings, convert value array back to string
        if (f.multi) {
          if (form[f.field]) {
            entity[f.idKey] = form[f.field].map(v => _.toString(v)).join(',');
          }
        } else {
          entity[f.idKey] = form[f.field];
        }
      } else {
        entity[f.field] = form[f.field];
      }
    });
    if (mapping.multipleEntities) {
      entity.PsaInstance = form.PsaInstance;
    }

    return entity;
  }

  const createEntity = async form => {
    const entity = buildEntityFromForm(form);
    const response = await createLiftoffMapping(entityType, entity);
    handleResponse(response, entityType, "create")
    await load();
  };

  const updateEntity = async form => {
    const entity = buildEntityFromForm(form);
    const response = await updateLiftoffMapping(entityType, form.ID, entity);
    handleResponse(response, entityType, "update")
    await load();
  };

  const removeEntity = async e => {
    const response = await deleteLiftoffMapping(entityType, e.ID);
    handleResponse(response, entityType, "remove")
    await load();
  };

  const filterData = useCallback((data, instance) => {
    let filtered
    if (mapping.multipleEntities) {
      filtered = data.filter(d => d.PsaInstance === instance);
    } else {
      filtered = data;
    }
    setFilteredData(filtered);
  }, [mapping.multipleEntities]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getLiftoffMapping(entityType);

      // Get all entity values for each mapping field
      const promises = [];
      const mappingData = {};
      const entities = [];

      // Make sure to load each entity only once
      mapping.fields.forEach(f => {
        if (f.type === 'mapping' && !entities.includes(f.entity)) {
          promises.push(getLiftoffMapping(f.entity).then(data => {
            mappingData[f.field] = data;
          }));
          entities.push(f.entity);
        }
      });

      // Build mapping data
      await Promise.all(promises);
      setMappingData(mappingData);

      // Build table rows
      const data = buildRowsFromResponse(res, mappingData);
      setData(data);
      filterData(data, instance)
      if (mapping.warningFunction) {
        mapping.warningFunction(setWarning, data, mappingData);
      }
      if (mapping.help) {
        setHelp(mapping.help);
      }
    } catch (e) {
      console.error(`Could not get liftoff mapping data`, e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title={`${group.title} - ${mapping.title}`}
            subTitle={`(${data.length} shown)`}
            style={{padding: 0}}
          />

          <p>{help}</p>
        </Col>
      </Row>
      <Row style={{marginBottom: 10}}>
          {mapping.multipleEntities ?
            (<Col style={{marginRight: 10}}>
              <Select
                value={instance}
                style={{width: 120}}
                placeholder="Select Instance"
                onChange={(v) => {
                  setInstance(v);
                  filterData(data,v)
                }}
                options={PSA_INSTANCE}
                />
            </Col>) :
            ''}
        <Col>
          <Checkbox
            checked={showAuditing}
            onChange={(e) => setShowAuditing(e.target.checked)}
          >
            Show Auditing
          </Checkbox>
        </Col>
        <Col flex="auto">
          <Button
            style={{float: 'right'}}
            onClick={() => load()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          <CreateLiftoffMappingButton
            title={mapping.title}
            fields={mapping.fields}
            mappingData={mappingData}
            onComplete={createEntity}
            psa_instance={mapping.multipleEntities ? instance : null}
            style={{float: 'right', marginRight: 10}}/>
        </Col>
      </Row>
      <Row>
        <div style={{marginTop: -10, marginBottom: 5, color: '#ff4d4f'}}>{warning}</div>
      </Row>
      <Table
        ref={tableContainerRef}
        rowKey="ID"
        dataSource={filteredData}
        columns={columns}
        pagination={false}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
    </>);
};

export {LiftoffMapping};
